import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";

import { HUB_TOAST_Z_INDEX } from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

export type HubToastTone = "error" | "success" | "info" | "warning";

export type HubToastInput = {
  message: string | ReactNode;
  tone?: HubToastTone;
};

type HubToastItem = {
  id: string;
  message: string | ReactNode;
  tone: HubToastTone;
};

type HubToastContextValue = {
  showToast: (input: HubToastInput) => void;
  dismissToast: (id: string) => void;
};

const HubToastContext = createContext<HubToastContextValue | null>(null);

export const HUB_TOAST_AUTO_DISMISS_MS = 16_000;

const AUTO_DISMISS_MS = HUB_TOAST_AUTO_DISMISS_MS;

const toneClasses: Record<HubToastTone, { shell: string; icon: string }> = {
  error: {
    shell: "border-rose-200 bg-rose-50 text-rose-950",
    icon: "text-rose-700",
  },
  success: {
    shell: "border-emerald-200 bg-emerald-50 text-emerald-950",
    icon: "text-emerald-700",
  },
  info: {
    shell: "border-slate-200 bg-white text-slate-900 shadow-sm",
    icon: "text-slate-500",
  },
  warning: {
    shell: "border-amber-200 bg-amber-50 text-amber-950",
    icon: "text-amber-700",
  },
};

function HubToastIcon({ tone }: { tone: HubToastTone }): ReactNode {
  const className = cn("mt-0.5 h-4 w-4 shrink-0", toneClasses[tone].icon);
  if (tone === "success") {
    return <CheckCircle2 className={className} aria-hidden />;
  }
  if (tone === "error") {
    return <AlertTriangle className={className} aria-hidden />;
  }
  if (tone === "warning") {
    return <AlertTriangle className={className} aria-hidden />;
  }
  return <Info className={className} aria-hidden />;
}

function HubToastCard({
  toast,
  onDismiss,
}: {
  toast: HubToastItem;
  onDismiss: (id: string) => void;
}): ReactNode {
  return (
    <div
      className={cn(
        "pointer-events-auto flex w-full max-w-md items-start gap-2 rounded-md border px-3 py-2 text-sm shadow-md",
        toneClasses[toast.tone].shell,
      )}
      role={toast.tone === "error" || toast.tone === "warning" ? "alert" : "status"}
    >
      <HubToastIcon tone={toast.tone} />
      <span className="min-w-0 flex-1 leading-snug">{toast.message}</span>
      <button
        type="button"
        className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss message"
      >
        <XCircle className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export function HubToastProvider({ children }: PropsWithChildren): ReactNode {
  const [toasts, setToasts] = useState<HubToastItem[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer != null) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (input: HubToastInput) => {
      const id = crypto.randomUUID();
      const tone = input.tone ?? "info";
      setToasts((current) => [...current, { id, message: input.message, tone }]);
      const timer = setTimeout(() => dismissToast(id), AUTO_DISMISS_MS);
      timersRef.current.set(id, timer);
    },
    [dismissToast],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  const value = useMemo(() => ({ showToast, dismissToast }), [dismissToast, showToast]);

  return (
    <HubToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 flex flex-col items-end gap-2 p-4 sm:p-6"
        style={{ zIndex: HUB_TOAST_Z_INDEX }}
        aria-live="polite"
        aria-relevant="additions"
      >
        {toasts.map((toast) => (
          <HubToastCard key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </HubToastContext.Provider>
  );
}

export function useHubToast(): HubToastContextValue {
  const value = useContext(HubToastContext);
  if (value == null) {
    throw new Error("useHubToast must be used within HubToastProvider.");
  }
  return value;
}
